import { CircularProgress, styled, useTheme } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useMemo, useState } from 'react';
import { Typography } from '../../components/Typography/Typography';
import { useWorkflows } from '../../queries/hooks/useWorkflows';
import { isSmallScreenState } from '../../state/UIState';
import { PageHeader } from '../layout/PageHeader';
import { ContentWrapper } from '../layout/styled';
import { WorkflowDetail } from './components/WorkflowDetail';
import { byWorkflowOrder } from '../../utils/workflowOrder';
import { Workflow } from '../../types';

const SUBMITTER_TOKEN = 'SUBMITTER';
// Group headings are the submitter family; the Submitter-node override is the singular role name.
const GROUP_HEADING: Record<string, string> = { 'Asset Servicer': 'Asset Services' };

type WorkflowGroup = { heading: string; workflows: Workflow[] };

/**
 * Read-only, admin-only overview of every approval workflow. Master–detail layout: a selectable list
 * of workflow names on the left, grouped by submitter type (Operating Partner / Asset Services), and
 * a detail pane on the right showing the selected workflow's approval pipeline (Submitter -> stages ->
 * Closed) and its leave & non-deal (default) rows. Data comes from `/workflow`; no mutations.
 */
export const Workflows = () => {
  const { colors } = useTheme();
  const isSmallScreen = useAtomValue(isSmallScreenState);
  const { data: workflows, isLoading } = useWorkflows();
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const sortedWorkflows = useMemo(
    () => [...(workflows ?? [])].sort(byWorkflowOrder),
    [workflows]
  );

  // Groups keep the client-defined order: a group appears where its first workflow does.
  const groups = useMemo(() => {
    const byHeading = new Map<string, WorkflowGroup>();
    for (const workflow of sortedWorkflows) {
      const label = workflow.stageLabels?.[SUBMITTER_TOKEN]?.trim();
      const heading = label ? GROUP_HEADING[label] ?? label : 'Other';
      if (!byHeading.has(heading)) byHeading.set(heading, { heading, workflows: [] });
      byHeading.get(heading)?.workflows.push(workflow);
    }
    return [...byHeading.values()];
  }, [sortedWorkflows]);

  const selectedWorkflow = useMemo(
    () => sortedWorkflows.find((workflow) => workflow.id === selectedId) ?? sortedWorkflows[0],
    [sortedWorkflows, selectedId]
  );

  return (
    <>
      <PageHeader
        title='Workflows'
        description='View each approval workflow and its stages'
        breadcrumbs={[{ title: 'Administration' }, { title: 'Workflows' }]}
        titleEndAdornment={
          <ReadOnlyPill style={{ backgroundColor: colors.surfaceStatus.noAction }}>
            <Typography variant='caption' color={colors.text.secondary}>
              Read-only
            </Typography>
          </ReadOnlyPill>
        }
      />
      <ContentWrapper isSmallScreen={isSmallScreen}>
        {isLoading ? (
          <CenterState>
            <CircularProgress sx={{ color: colors.surfaceAccent.default }} />
          </CenterState>
        ) : !selectedWorkflow ? (
          <CenterState>
            <Typography variant='body' color={colors.text.secondary}>
              No workflows to display.
            </Typography>
          </CenterState>
        ) : (
          <Layout $isSmallScreen={isSmallScreen}>
            <Sidebar
              $isSmallScreen={isSmallScreen}
              style={{
                backgroundColor: colors.surfaceBackground.bg1,
                border: `1px solid ${colors.border.default}`,
              }}
            >
              {groups.map((group) => (
                <Group key={group.heading}>
                  <GroupHeading variant='overline' color={colors.text.caption}>
                    {group.heading}
                  </GroupHeading>
                  {group.workflows.map((workflow) => {
                    const isActive = workflow.id === selectedWorkflow.id;
                    return (
                      <SidebarItem
                        key={workflow.id}
                        type='button'
                        $active={isActive}
                        onClick={() => setSelectedId(workflow.id)}
                        style={{
                          backgroundColor: isActive
                            ? colors.surfaceBackground.highlighted
                            : 'transparent',
                          borderLeft: `3px solid ${
                            isActive ? colors.surfaceAccent.default : 'transparent'
                          }`,
                        }}
                      >
                        <Typography
                          variant={isActive ? 'subtitle1' : 'body'}
                          color={isActive ? colors.textAccent.default : colors.text.main}
                        >
                          {workflow.name}
                        </Typography>
                      </SidebarItem>
                    );
                  })}
                </Group>
              ))}
            </Sidebar>

            <DetailArea>
              <WorkflowDetail workflow={selectedWorkflow} />
            </DetailArea>
          </Layout>
        )}
      </ContentWrapper>
    </>
  );
};

const Layout = styled('div')<{ $isSmallScreen: boolean }>`
  display: flex;
  flex-direction: ${({ $isSmallScreen }) => ($isSmallScreen ? 'column' : 'row')};
  gap: 24px;
  align-items: stretch;
`;

const Sidebar = styled('div')<{ $isSmallScreen: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px;
  border-radius: 14px;
  flex-shrink: 0;
  width: ${({ $isSmallScreen }) => ($isSmallScreen ? '100%' : '380px')};
  max-height: ${({ $isSmallScreen }) => ($isSmallScreen ? '220px' : 'none')};
  overflow-y: ${({ $isSmallScreen }) => ($isSmallScreen ? 'auto' : 'visible')};
  align-self: flex-start;
`;

const SidebarItem = styled('button')<{ $active: boolean }>`
  display: flex;
  align-items: center;
  text-align: left;
  width: 100%;
  padding: 12px 14px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  &:hover {
    background-color: ${({ theme, $active }) =>
      $active ? theme.colors.surfaceBackground.highlighted : theme.colors.surfaceInteraction.hover};
  }
`;

const Group = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 4px;
  & + & {
    margin-top: 12px;
  }
`;

const GroupHeading = styled(Typography)`
  text-transform: uppercase;
  letter-spacing: 0.8px;
  padding: 4px 14px;
  margin-bottom: 0;
`;

const ReadOnlyPill = styled('span')`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 12px;
  white-space: nowrap;
`;

const DetailArea = styled('div')`
  flex: 1;
  min-width: 0;
`;

const CenterState = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 64px 0;
`;
